/**
 * Guide Me — host-side locator alias registry.
 *
 * Plugins publish a {@link TourLocatorVocabulary} (keyed by stable
 * semantic names like `'idp-users.create-btn'`) via
 * {@link registerVocabulary}. Tours reference aliases through
 * `@plugin.<plugin-id>:<name>` (qualified) or `@:<name>` (implicit
 * active plugin); the runtime resolves through this registry at
 * step time.
 *
 * Why a registry rather than direct imports: it inverts the
 * dependency. The portable parser knows nothing about plugins; the
 * host doesn't statically import every plugin's locators (which
 * would defeat code-splitting). Each plugin registers its
 * vocabulary at module-eval time when the plugin's bundle loads.
 */

import type { Locator, TourLocatorVocabulary } from '@empowernow/etl-tour';

const VOCABULARIES = new Map<string, TourLocatorVocabulary>();

/**
 * Registers (or replaces) a plugin's locator vocabulary. Idempotent
 * — calling twice with the same plugin id replaces the previous
 * vocabulary (useful for HMR during dev).
 *
 * Returns an unregister callback so plugins that mount/unmount can
 * clean up. Most plugins call once at module-eval time and never
 * unregister.
 */
export function registerVocabulary(
  pluginId: string,
  vocabulary: TourLocatorVocabulary,
): () => void {
  if (!pluginId) {
    throw new Error('locatorRegistry: pluginId must be a non-empty string');
  }
  VOCABULARIES.set(pluginId, vocabulary);
  return () => {
    if (VOCABULARIES.get(pluginId) === vocabulary) {
      VOCABULARIES.delete(pluginId);
    }
  };
}

/**
 * Resolves an alias to a concrete {@link Locator}. The `pluginId`
 * argument supplies the active plugin for `@:<name>` (where
 * `alias.plugin === ''`); fully-qualified aliases ignore it.
 *
 * Returns `null` when the vocabulary or alias is missing — the
 * caller decides whether to throw (runtime resolution) or surface as
 * a CI failure (lint pass).
 */
export function resolveAlias(
  alias: { plugin: string; name: string },
  activePluginId: string | null | undefined,
): Locator | null {
  const pluginId = alias.plugin || activePluginId || '';
  if (!pluginId) return null;
  const vocab = VOCABULARIES.get(pluginId);
  if (!vocab) return null;
  return vocab[alias.name] ?? null;
}

/** Returns the set of registered plugin ids (for diagnostics / CI). */
export function listRegisteredPlugins(): ReadonlyArray<string> {
  return Array.from(VOCABULARIES.keys());
}

/** Returns a plugin's vocabulary (or null). Used by CI lint passes. */
export function getVocabulary(pluginId: string): TourLocatorVocabulary | null {
  return VOCABULARIES.get(pluginId) ?? null;
}

/** Test-only: clear every registered vocabulary. */
export function __clearRegistry(): void {
  VOCABULARIES.clear();
}
